import React from 'react';
import { Badge } from '../ui/badge';
import { AlertTriangle, AlertCircle, CheckCircle, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Thresholds for red flags
const THRESHOLDS = {
  beneish: -2.22,    // M-Score > -2.22 = manipulation risk (Beneish 1999)
  altman: 1.1,       // Z''-Score < 1.1 = bankruptcy risk (Altman 1993, EM variant)
  piotroski: 4,      // F-Score < 4 = weak fundamentals
};

// Analyze quality data and return flag status
export const analyzeQualityFlags = (qualityData) => {
  if (!qualityData || !qualityData.analyses) {
    return { status: 'unknown', flags: [] };
  }

  const { analyses } = qualityData;
  const flags = [];
  
  // Check Beneish M-Score (manipulation risk)
  if (analyses.beneish?.score !== undefined && analyses.beneish.score !== null) {
    if (analyses.beneish.score > THRESHOLDS.beneish) {
      flags.push({
        type: 'critical',
        label: 'Manipulation',
        detail: `Beneish M-Score: ${analyses.beneish.score.toFixed(2)} (> ${THRESHOLDS.beneish})`,
        tooltip: 'Risque élevé de manipulation comptable selon le modèle Beneish'
      });
    }
  }
  
  // Check Altman Z''-Score (bankruptcy risk)
  if (analyses.altman?.score !== undefined && analyses.altman.score !== null) {
    if (analyses.altman.score < THRESHOLDS.altman) {
      flags.push({
        type: 'critical',
        label: 'Détresse',
        detail: `Altman Z''-Score: ${analyses.altman.score.toFixed(2)} (< ${THRESHOLDS.altman})`,
        tooltip: 'Risque élevé de faillite selon le modèle Altman'
      });
    }
  }
  
  // Check Piotroski F-Score (weak fundamentals)
  if (analyses.piotroski?.score !== undefined && analyses.piotroski.score !== null) {
    if (analyses.piotroski.score < THRESHOLDS.piotroski) {
      flags.push({
        type: 'warning',
        label: 'Faible',
        detail: `Piotroski F-Score: ${analyses.piotroski.score}/9 (< ${THRESHOLDS.piotroski})`,
        tooltip: 'Fondamentaux financiers faibles selon le score Piotroski'
      });
    }
  }
  
  // Determine overall status
  const hasCritical = flags.some(f => f.type === 'critical');
  const hasWarning = flags.some(f => f.type === 'warning');
  
  let status = 'ok';
  if (hasCritical) status = 'critical';
  else if (hasWarning) status = 'warning';
  
  return { status, flags };
};

// Compact badge for Screener table
export const RedFlagBadgeCompact = ({ qualityData, showLabel = true }) => {
  const { status, flags } = analyzeQualityFlags(qualityData);
  
  if (status === 'unknown') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className="bg-gray-500/10 border-gray-500/30 text-gray-400 text-xs px-1.5 py-0.5">
              <HelpCircle className="w-3 h-3" />
              {showLabel && <span className="ml-1">N/A</span>}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="bg-gray-900 border-gray-700 text-white">
            <p className="text-sm">Données insuffisantes pour l'analyse</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  if (status === 'ok') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-400 text-xs px-1.5 py-0.5">
              <CheckCircle className="w-3 h-3" />
              {showLabel && <span className="ml-1">OK</span>}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="bg-gray-900 border-gray-700 text-white">
            <p className="text-sm">Aucun signal d'alerte comptable détecté</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  // Critical or Warning
  const isCritical = status === 'critical';
  const primaryFlag = flags[0];
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge 
            variant="outline" 
            className={`text-xs px-1.5 py-0.5 ${
              isCritical 
                ? 'bg-red-500/10 border-red-500/30 text-red-400' 
                : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
            }`}
          >
            {isCritical ? <AlertCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {showLabel && <span className="ml-1">{primaryFlag.label}</span>}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="bg-gray-900 border-gray-700 text-white max-w-xs">
          <div className="space-y-2">
            {flags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2">
                {flag.type === 'critical' 
                  ? <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                }
                <div>
                  <p className={`text-sm font-medium ${flag.type === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                    {flag.detail}
                  </p>
                  <p className="text-xs text-gray-400">{flag.tooltip}</p>
                </div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Larger badge for StockDetail header
export const RedFlagBadgeHeader = ({ qualityData, onClick }) => {
  const { status, flags } = analyzeQualityFlags(qualityData);
  
  if (status === 'unknown' || status === 'ok') {
    return null; // Don't show badge if OK or unknown
  }
  
  const isCritical = status === 'critical';
  const primaryFlag = flags[0];
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              isCritical 
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30' 
                : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30'
            }`}
            data-testid="red-flag-badge-header"
          >
            {isCritical ? <AlertCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            <span>{flags.length} {flags.length > 1 ? 'Alertes' : 'Alerte'}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
          <div className="space-y-2">
            <p className="text-sm font-medium text-white mb-2">Signaux d'alerte comptable :</p>
            {flags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2">
                {flag.type === 'critical' 
                  ? <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                }
                <div>
                  <p className={`text-sm font-medium ${flag.type === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                    {flag.detail}
                  </p>
                  <p className="text-xs text-gray-400">{flag.tooltip}</p>
                </div>
              </div>
            ))}
            <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-700">
              Cliquez pour voir les détails
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default RedFlagBadgeCompact;
